/* eslint-disable @typescript-eslint/no-explicit-any */
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Material from '@primeuix/themes/material';

import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { routes } from './app.routes';
import { AppTitleStrategy } from './core/services/app-title-strategy.service';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withFetch,
} from '@angular/common/http';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  InteractionStatus,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { CustomHeadersInterceptor } from './core/interceptors/custom-headers.interceptor';
import { RetryInterceptor } from './core/interceptors/retry.interceptor';
import { IMAGE_CONFIG } from '@angular/common';
import { MessageService } from 'primeng/api';
import { LoadingInterceptor } from '@ao/shared/interceptor/loading.interceptor';
import { ErrorService } from '@ao/shared/services/error-service';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { HttpLoggingInterceptor } from './core/interceptors/payload.interceptor';
import { filter, take } from 'rxjs';
import { SecureStorageService } from './core/services/secure-storage.service';

// Initialize Application Insights with secure storage
const secureStorageInstance = new SecureStorageService();
const appInsightsConnectionString =
  environment.applicationInsights.connectionString;

// The connection string is injected at container startup via envsubst in start-nginx.sh.
// If envsubst didn't run (local ng serve) or the env var was missing, the value will
// still contain the '${...}' placeholder or the untouched default — skip SDK init in that case.
const rawConnectionString =
  environment.applicationInsights.connectionString ?? '';
const isValidAppInsightsConn =
  !!rawConnectionString &&
  !rawConnectionString.includes('${') &&
  rawConnectionString !== 'ENTER_APP_INSIGHTS_CONNECTION_STRING';

const appInsights = new ApplicationInsights({
  config: {
    connectionString: isValidAppInsightsConn ? rawConnectionString : '',
    enableAutoRouteTracking: true,
    loggingLevelConsole: environment.production ? 0 : 2,
    loggingLevelTelemetry: 2,
    disableFetchTracking: false,
    enableCorsCorrelation: true,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    // Qualys QID 150476: avoid setting ai_user/ai_session cookies pre-consent.
    // `disableCookiesUsage` blocks the SDK cookie manager; `cookieCfg.enabled = false`
    // is kept as defence-in-depth.
    disableCookiesUsage: true,
    cookieCfg: {
      enabled: false,
    },
  },
});

if (isValidAppInsightsConn) {
  // Configure telemetry initializer with secure storage
  appInsights.addTelemetryInitializer((envelope: any) => {
    envelope.tags['ai.cloud.role'] = 'ao-frontend-ui';

    // Retrieve user information from secure storage instead of localStorage
    const userName =
      secureStorageInstance.getItem('loggedInUserName') || 'anonymous';
    let correlationId = secureStorageInstance.getItem('correlationId');

    // Generate and store correlation ID if it doesn't exist
    if (!correlationId) {
      correlationId = self.crypto.randomUUID();
      secureStorageInstance.setItem('correlationId', correlationId);
    }

    envelope.tags['ai.user.id'] = userName;
    envelope.tags['ai.operation.id'] = correlationId;

    return true;
  });

  appInsights.loadAppInsights();

  // Set authenticated user context from secure storage
  const authenticatedUser = secureStorageInstance.getItem('loggedInUserName');
  if (authenticatedUser) {
    appInsights.setAuthenticatedUserContext(authenticatedUser);
  }
} else if (!environment.production) {
  // eslint-disable-next-line no-console
  console.warn(
    '[AppInsights] Connection string not provided — telemetry disabled.'
  );
}

export { appInsights };

export function msalInitializer(
  msalService: MsalService,
  broadcastService: MsalBroadcastService
): () => Promise<void> {
  return () => {
    return new Promise<void>(resolve => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          const accounts = msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            msalService.instance.setActiveAccount(accounts[0]);
          }
          resolve();
        }
      }, 5000);

      msalService.handleRedirectObservable().subscribe({
        next: result => {
          if (result) {
            msalService.instance.setActiveAccount(result.account);
          }
        },
        error: error => console.error('Redirect error:', error),
        complete: () => {
          broadcastService.inProgress$
            .pipe(
              filter((status: InteractionStatus) => {
                return status === InteractionStatus.None;
              }),
              take(1)
            )
            .subscribe(() => {
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                  msalService.instance.setActiveAccount(accounts[0]);
                }
                resolve();
              }
            });
        },
      });
    });
  };
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      secureCookies: true,
      // Qualys QIDs 150121 & 150123: JS-set cookies cannot carry HttpOnly.
      // Keep MSAL auth state in localStorage only; no msal.* cookies are emitted.
      storeAuthStateInCookie: false,
    },
    system: {
     // allowNativeBroker: false,
      preventCorsPreflight: true,
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(
    environment.apiConfig.uri,
    environment.apiConfig.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/dashboard',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: msalInitializer,
      deps: [MsalService, MsalBroadcastService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpLoggingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHeadersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true,
    },
    MessageService,
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Material,
        options: {
          prefix: 'p',
          darkModeSelector: 'false',
          cssLayer: false,
        },
      },
      ripple: false,
      csp: {
        nonce: '...',
      },
    }),
    {
      provide: IMAGE_CONFIG,
      useValue: {
        disableImageSizeWarning: true,
        disableImageLazyLoadWarning: true,
      },
    },
  ],
};
